// ABOUTME: React Query hook for fetching retarder configuration from the backend API.
// ABOUTME: Calls GET /tuc-configs/retarder, finds config for selected scope, and transforms to form-ready shape.

import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'

import {api} from '../../../../App.store'
import {RetarderApiResponse, RetarderConfig, RetarderFormValues} from '../types/retarder'
import {asNotInherited, emptyApiRow} from '../utils/emptyConfig'

const extractRetarderValues = (response: RetarderApiResponse): RetarderFormValues => ({
  min: response.min?.value ?? 0,
  max: response.max?.value ?? 0,
  step: response.step?.value ?? 0,
  uom: response.uom?.value?.displaySymbol ?? '',
  uomId: response.uom?.value?.id,
})

const transformResponse = (response: RetarderApiResponse, parent?: RetarderFormValues): RetarderConfig => {
  const own = extractRetarderValues(response)
  return {
    id: response.id,
    ...own,
    overrides: {
      uom: response.uom?.isOverridden ?? false,
      min: response.min?.isOverridden ?? false,
      max: response.max?.isOverridden ?? false,
      step: response.step?.isOverridden ?? false,
    },
    parentValues: parent ?? own,
    _meta: {
      scopeId: response.scopeId,
      isInherited: !(
        (response.min?.isOverridden ?? false) ||
        (response.max?.isOverridden ?? false) ||
        (response.step?.isOverridden ?? false) ||
        (response.uom?.isOverridden ?? false)
      ),
      resolvedFrom: null,
      createdAt: response._meta.createdAt,
      updatedAt: response._meta.updatedAt,
      createdBy: response._meta.createdBy,
      updatedBy: response._meta.updatedBy,
    },
  }
}

export const useRetarderConfig = (
  countryId: string | undefined,
  businessLine: string | undefined,
  scopeId: string | undefined,
  rootScopeId?: string | undefined
): {data: RetarderConfig | undefined; isLoading: boolean; error: Error | null} => {
  const {data: rawData, isLoading, error} = useQuery<RetarderApiResponse[], Error>({
    queryKey: ['retarderConfig', countryId, businessLine],
    queryFn: async () => {
      const res = await api.get<RetarderApiResponse[]>('/tuc-configs/retarder', {
        params: {countryId, businessLine: businessLine!.toUpperCase()},
      })
      return res.data ?? []
    },
    enabled: Boolean(countryId && businessLine && scopeId),
    staleTime: 30_000,
  })

  const data = useMemo(() => {
    if (!rawData || !scopeId) return undefined

    const rootConfig = rootScopeId
      ? rawData.find((r) => r.scopeId === rootScopeId)
      : undefined
    const parentValues = rootConfig ? extractRetarderValues(rootConfig) : undefined

    const match = rawData.find((r) => r.scopeId === scopeId)
    if (match) return transformResponse(match, parentValues)

    if (!rootConfig) return asNotInherited(transformResponse(emptyApiRow<RetarderApiResponse>(scopeId)))

    const inherited = transformResponse(rootConfig)
    return {
      ...inherited,
      id: undefined,
      _meta: {...inherited._meta, isInherited: true},
      overrides: {uom: false, min: false, max: false, step: false},
    }
  }, [rawData, scopeId, rootScopeId])

  return {data, isLoading, error}
}
